'use client';

import { useState, useEffect } from 'react';
import { RichButton } from '@/components/rich-button';
import { getHapticsEnabled, setHapticsEnabled, triggerHaptic } from '../lib/haptics';

interface SettingsProps {
  onResetOptions?: () => void;
  onResetHistory?: () => void;
}

export default function SettingsComponent({ onResetOptions, onResetHistory }: SettingsProps) {
  const [hapticsOn, setHapticsOn] = useState(true);
  const [soundOn, setSoundOn] = useState(true);

  useEffect(() => {
    setHapticsOn(getHapticsEnabled());
    const stored = localStorage.getItem('sound-enabled');
    setSoundOn(stored !== 'false');
  }, []);

  const toggleHaptics = () => {
    const next = !hapticsOn;
    setHapticsOn(next);
    setHapticsEnabled(next);
    if (next) triggerHaptic('light');
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem('sound-enabled', String(next));
  };

  return (
    <div className="space-y-4">
      {/* Preferences */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-medium text-muted-foreground mb-4">preferences</h3>
        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer group">
            <span className="text-sm">sound effects</span>
            <button
              role="switch"
              aria-checked={soundOn}
              onClick={toggleSound}
              className={`relative w-10 h-6 rounded-full transition-colors duration-200 ${soundOn ? 'bg-primary' : 'bg-muted-foreground/30'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-foreground dark:bg-muted shadow-sm border border-border transition-transform duration-200 ${soundOn ? 'translate-x-4 bg-primary-foreground dark:bg-primary-foreground !border-transparent' : 'translate-x-0'}`} />
            </button>
          </label>
          <label className="flex items-center justify-between cursor-pointer group">
            <span className="text-sm">haptic feedback</span>
            <button
              role="switch"
              aria-checked={hapticsOn}
              onClick={toggleHaptics}
              className={`relative w-10 h-6 rounded-full transition-colors duration-200 ${hapticsOn ? 'bg-primary' : 'bg-muted-foreground/30'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-foreground dark:bg-muted shadow-sm border border-border transition-transform duration-200 ${hapticsOn ? 'translate-x-4 bg-primary-foreground dark:bg-primary-foreground !border-transparent' : 'translate-x-0'}`} />
            </button>
          </label>
        </div>
      </div>

      {/* Data management */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-medium text-muted-foreground mb-3">data management</h3>
        <div className="flex items-center gap-3">
          <RichButton
            onClick={onResetOptions}
            color="default"
            size="sm"
          >
            reset options
          </RichButton>
          <RichButton
            onClick={onResetHistory}
            color="default"
            size="sm"
          >
            reset history
          </RichButton>
        </div>
      </div>
    </div>
  );
}
